
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';
import { useFolderList, useCreateFolder, useDeleteFolder } from '../../../queries/folders';
import { usePdfFields, useUpdatePdfFolder } from '../../../queries/pdfs';

interface PdfData {
  id: string;
  file_name: string;
  folder_id?: string | null;
}

const FOLDER_COLORS = [
  { value: '#6366F1', label: '인디고' },
  { value: '#8B5CF6', label: '보라' },
  { value: '#EC4899', label: '핑크' },
  { value: '#EF4444', label: '빨강' },
  { value: '#F97316', label: '주황' },
  { value: '#EAB308', label: '노랑' },
  { value: '#22C55E', label: '초록' },
  { value: '#14B8A6', label: '청록' },
];

export default function FolderPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pdfId = searchParams.get('id');
  const [userId, setUserId] = useState<string | null>(null);
  const { data: pdfData = null, isLoading: pdfLoading } = usePdfFields<PdfData>(pdfId, 'id, file_name, folder_id');
  const { data: folders = [] } = useFolderList(userId);
  const createFolderMutation = useCreateFolder();
  const deleteFolderMutation = useDeleteFolder();
  const updatePdfFolderMutation = useUpdatePdfFolder();
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [isAddingFolder, setIsAddingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderColor, setNewFolderColor] = useState('#6366F1');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const loading = pdfLoading;

  /** 폴더 삭제 확인 모달 상태 */
  const [deleteFolderConfirmModal, setDeleteFolderConfirmModal] = useState<{ isOpen: boolean; folderId: string | null; folderName: string }>({
    isOpen: false,
    folderId: null,
    folderName: '',
  });

  useEffect(() => {
    if (!pdfId) {
      navigate('/dashboard');
      return;
    }
    const checkAuth = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          navigate('/login');
          return;
        }
        setUserId(user.id);
      } catch {
        navigate('/login');
      }
    };
    checkAuth();
  }, [pdfId, navigate]);

  useEffect(() => {
    if (pdfData?.folder_id) {
      setSelectedFolder(pdfData.folder_id);
    }
  }, [pdfData]);

  const handleAddFolder = async () => {
    if (!newFolderName.trim()) return;
    if (!userId) return;
    await createFolderMutation.mutateAsync({ userId, name: newFolderName.trim(), color: newFolderColor });
    setNewFolderName('');
    setNewFolderColor('#6366F1');
    setIsAddingFolder(false);
  };

  /**
   * 폴더 삭제 확인 모달을 엽니다.
   * @param folderId - 삭제할 폴더 ID
   */
  const handleDeleteFolder = (folderId: string) => {
    const folder = folders.find((f) => f.id === folderId);
    setDeleteFolderConfirmModal({ isOpen: true, folderId, folderName: folder?.name ?? '' });
  };

  /** 폴더 삭제 확인 후 실제 삭제를 수행합니다. */
  const confirmDeleteFolder = async () => {
    if (!deleteFolderConfirmModal.folderId) return;
    await deleteFolderMutation.mutateAsync(deleteFolderConfirmModal.folderId);
    if (selectedFolder === deleteFolderConfirmModal.folderId) {
      setSelectedFolder(null);
    }
    setDeleteFolderConfirmModal({ isOpen: false, folderId: null, folderName: '' });
  };

  const handleSave = async () => {
    if (!pdfId) return;
    setSaving(true);
    try {
      await updatePdfFolderMutation.mutateAsync({ pdfId, folderId: selectedFolder });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (err) {
      console.error('저장 오류:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-3 border-indigo-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 폴더 삭제 확인 모달 */}
      {deleteFolderConfirmModal.isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setDeleteFolderConfirmModal({ isOpen: false, folderId: null, folderName: '' })}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4 animate-in fade-in zoom-in duration-200">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-folder-line text-2xl text-red-500"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-900 text-center mb-2">{t('dashboard.delete_folder_confirm')}</h3>
            <p className="text-sm text-gray-500 text-center mb-6">
              {t('dashboard.delete_folder_name', { name: deleteFolderConfirmModal.folderName })}
              <br />
              {t('dashboard.delete_folder_warning')}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteFolderConfirmModal({ isOpen: false, folderId: null, folderName: '' })}
                className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-all cursor-pointer whitespace-nowrap"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={confirmDeleteFolder}
                className="flex-1 px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl font-medium transition-all cursor-pointer whitespace-nowrap"
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 헤더 */}
      <header className="bg-white border-b border-gray-200 px-4 sm:px-8 py-4 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="w-9 h-9 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-lg transition-all cursor-pointer"
          >
            <i className="ri-arrow-left-line text-lg text-gray-600"></i>
          </button>
          <div>
            <h1 className="text-lg font-bold text-gray-900">{t('dashboard.folder_move')}</h1>
            <p className="text-xs text-gray-500">{pdfData?.file_name?.replace('.pdf', '')}</p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
              {t('common.saving')}
            </>
          ) : saveSuccess ? (
            <>
              <i className="ri-check-line"></i>
              {t('common.save_complete')}
            </>
          ) : (
            <>
              <i className="ri-save-line"></i>
              {t('common.save')}
            </>
          )}
        </button>
      </header>

      <div className="max-w-2xl mx-auto p-4 sm:p-8">
        {/* 현재 파일 정보 */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-file-pdf-line text-2xl text-indigo-500"></i>
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-gray-900 text-sm truncate">{pdfData?.file_name?.replace('.pdf', '')}</h3>
              <p className="text-xs text-gray-500">
                {selectedFolder
                  ? t('dashboard.current_folder', { name: folders.find(f => f.id === selectedFolder)?.name || '' })
                  : t('dashboard.no_folder')}
              </p>
            </div>
          </div>
        </div>

        {/* 폴더 선택 */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-base font-bold text-gray-900">{t('dashboard.select_folder')}</h2>
            <button
              onClick={() => setIsAddingFolder(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              {t('dashboard.new_folder')}
            </button>
          </div>

          {/* 폴더 미지정 옵션 */}
          <button
            onClick={() => setSelectedFolder(null)}
            className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl mb-2 transition-all cursor-pointer ${
              selectedFolder === null
                ? 'bg-indigo-50 border-2 border-indigo-300'
                : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
            }`}
          >
            <div className="w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center">
              <i className="ri-inbox-line text-gray-500"></i>
            </div>
            <span className={`text-sm font-medium ${selectedFolder === null ? 'text-indigo-700' : 'text-gray-700'}`}>
              {t('dashboard.no_folder')}
            </span>
            {selectedFolder === null && (
              <i className="ri-check-line text-indigo-500 ml-auto"></i>
            )}
          </button>

          {/* 폴더 목록 */}
          <div className="space-y-2">
            {folders.map((folder) => (
              <div
                key={folder.id}
                className={`group flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all cursor-pointer ${
                  selectedFolder === folder.id
                    ? 'bg-indigo-50 border-2 border-indigo-300'
                    : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                }`}
                onClick={() => setSelectedFolder(folder.id)}
              >
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: folder.color + '20' }}
                >
                  <i className="ri-folder-fill text-lg" style={{ color: folder.color }}></i>
                </div>
                <span className={`text-sm font-medium flex-1 ${
                  selectedFolder === folder.id ? 'text-indigo-700' : 'text-gray-700'
                }`}>
                  {folder.name}
                </span>
                {selectedFolder === folder.id && (
                  <i className="ri-check-line text-indigo-500"></i>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteFolder(folder.id);
                  }}
                  className="opacity-0 group-hover:opacity-100 w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-all cursor-pointer"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              </div>
            ))}
          </div>

          {/* 새 폴더 추가 */}
          {isAddingFolder && (
            <div className="mt-4 bg-gray-50 rounded-xl p-4 border border-gray-200">
              <div className="mb-3">
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAddFolder();
                    if (e.key === 'Escape') {
                      setIsAddingFolder(false);
                      setNewFolderName('');
                    }
                  }}
                  placeholder={t('dashboard.folder_name_placeholder')}
                  className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  autoFocus
                />
              </div>
              <div className="flex items-center gap-2 mb-3">
                {FOLDER_COLORS.map((color) => (
                  <button
                    key={color.value}
                    onClick={() => setNewFolderColor(color.value)}
                    className={`w-7 h-7 rounded-full transition-all cursor-pointer ${
                      newFolderColor === color.value ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : ''
                    }`}
                    style={{ backgroundColor: color.value }}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleAddFolder}
                  className="flex-1 px-3 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
                >
                  {t('common.add')}
                </button>
                <button
                  onClick={() => {
                    setIsAddingFolder(false);
                    setNewFolderName('');
                  }}
                  className="flex-1 px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
